import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  Input,
  Spinner,
  MenuList,
  MenuItem,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";

const useMenuStyles = makeStyles({
  menuContainer: {
    position: "absolute",
    top: "100%",
    left: 0,
    width: "100%",
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: "4px",
    zIndex: 10,
    maxHeight: "250px",
    overflowY: "auto",
  },
  menuItem: {
    padding: "8px 12px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
});

export default function TimeZoneSelector({ value = "", onSelect, isUpdate = false }) {
  const { t } = useTranslation();
  const styles = useMenuStyles();
  const [input, setInput] = useState(value);
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const inputRef = useRef(null);

  // Synchroniser input avec value quand value change
  React.useEffect(() => {
    if (value) {
      setInput(value);
    }
  }, [value]);

  // ✅ Load all timezones only once
  const allTimeZones = useMemo(() => moment.tz.names(), []);

  // ✅ Filter list efficiently
  const filteredZones = useMemo(() => {
    const query = input.trim().toLowerCase();
    return query
      ? allTimeZones.filter((z) => z.toLowerCase().includes(query))
      : allTimeZones;
  }, [input, allTimeZones]);

  const handleSelect = useCallback(
    (zone) => {
      setInput(zone);
      setShowMenu(false);
      onSelect?.(zone);
      inputRef.current?.blur();
    },
    [onSelect]
  );

  // ✅ Hide menu when clicking outside
  const handleClickOutside = useCallback((event) => {
    if (inputRef.current && !inputRef.current.contains(event.target)) {
      setShowMenu(false);
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  return (
    <div style={{ position: 'relative', width: '100%' }} ref={inputRef}>
      {isUpdate ? (
        <Input value={value} readOnly />
      ) : (
        <>
          <Input
            value={input}
            placeholder={t("SEARCH_TIMEZONE")}
            onFocus={() => setShowMenu(true)}
            onChange={(_, data) => {
              setInput(data.value);
              setShowMenu(true);
              setLoading(true);
              setTimeout(() => setLoading(false), 150);
            }}
          />
          {loading && (
            <Spinner size="tiny" style={{ position: "absolute", right: 8, top: 10 }} />
          )}
          {showMenu && filteredZones.length > 0 && (
            <div className={styles.menuContainer}>
              <MenuList>
                {filteredZones.map((zone) => (
                  <MenuItem
                    key={zone}
                    className={styles.menuItem}
                    onClick={() => handleSelect(zone)}
                  >
                    {zone}
                  </MenuItem>
                ))}
              </MenuList>
            </div>
          )}
          {showMenu && filteredZones.length === 0 && (
            <div className={styles.menuContainer}>
              <MenuList>
                <MenuItem disabled>No results found</MenuItem>
              </MenuList>
            </div>
          )}
        </>
      )}
    </div>
  );
}
